import { PlatformAvailability } from '../types/product'

interface PlatformPickerProps {
  availabilities: PlatformAvailability[]
  onChange: (slug: string, enabled: boolean) => void
  showSelectAll?: boolean
  disabled?: boolean
  helperText?: string
}

const PlatformPicker = ({
  availabilities,
  onChange,
  showSelectAll = false,
  disabled = false,
  helperText,
}: PlatformPickerProps) => {
  const sorted = [...availabilities].sort(
    (a, b) => a.platform.sortOrder - b.platform.sortOrder,
  )

  const allEnabled = sorted.length > 0 && sorted.every(a => a.enabled)
  const noneEnabled = sorted.every(a => !a.enabled)

  const setAll = (enabled: boolean) => {
    sorted.forEach(a => {
      if (a.enabled !== enabled) onChange(a.platform.slug, enabled)
    })
  }

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        No delivery platforms configured.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {showSelectAll && (
        <div className="flex gap-2 text-xs">
          <button
            type="button"
            onClick={() => setAll(true)}
            disabled={disabled || allEnabled}
            className="text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Select all
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={() => setAll(false)}
            disabled={disabled || noneEnabled}
            className="text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Select none
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {sorted.map(({ platform, enabled }) => (
          <div key={platform.slug} className="flex items-center">
            <input
              type="checkbox"
              id={`platform-${platform.slug}`}
              checked={enabled}
              disabled={disabled}
              onChange={e => onChange(platform.slug, e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-60"
            />
            <label
              htmlFor={`platform-${platform.slug}`}
              className="ml-2 block text-sm text-gray-700"
            >
              {platform.displayName}
            </label>
          </div>
        ))}
      </div>
      {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
    </div>
  )
}

export default PlatformPicker
